/* eslint-disable */
import React, { useEffect, useState } from "react";
import {
  Button,
  Grid,
  Group,
  NumberInput,
  Switch,
  Stack,
  Title,
  Divider,
  ActionIcon,
  Box,
  Modal,
  Text,
  Select,
} from "@mantine/core";
import { useForm, zodResolver } from "@mantine/form";
import { z } from "zod";
import { IconPlus, IconTrash } from "@tabler/icons-react";
import { useUser } from "hooks/useUser";
import { useUpdateLeaveConfiguration } from "api/leaveConfiguration/leaveConfigUpdate";
import { useGetLeaveConfiguration } from "api/leaveConfiguration/getLeaveConfig";
import { useGetRolePermissions } from "api/role/getRoles";
import { formatRoleName } from "utils/formatRoleName";

const defaultOrg = "66a99e2a83b5f1a87ef35df1";
const defaultUserId = "66a99e2a83b5f1a87ef35df2";

const serviceYearScaleItemSchema = z
  .object({
    role: z.string().min(1),
    minYears: z.number().int().nonnegative(),
    maxYears: z.number().int().nonnegative(),
    days: z.number().nonnegative(),
  })
  .refine((data) => data.maxYears >= data.minYears, {
    message: "Max Years must be >= Min Years",
  });

// carryForward is a fixed number of DAYS to carry into the next period
// (the backend caps it at the user's unused balance, i.e. min(days, unused)).
// e.g. 10 = carry over up to 10 unused days.
const carryForwardSchema = z.number().int().min(0).max(365);

const leaveConfigSchema = z.object({
  org: z.string().min(1),
  annualLeave: z.object({
    enabled: z.boolean(),
    baseAllocation: z.number().nonnegative(),
    proRateByStartDate: z.boolean(),
    scaleByServiceYears: z.boolean(),
    serviceYearScale: z.array(serviceYearScaleItemSchema),
    carryForward: carryForwardSchema,
    isReasonRequired: z.boolean(),
  }),
  sickLeave: z.object({
    enabled: z.boolean(),
    accrualRate: z.number().nonnegative(),
    maxAccrual: z.number().nonnegative(),
    gracePeriodDays: z.number().nonnegative(),
    requireCertification: z.boolean(),
    certificationAfterDays: z.number().nonnegative(),
    isReasonRequired: z.boolean(),
  }),
  pressingNecessity: z.object({
    enabled: z.boolean(),
    daysPerYear: z.number().nonnegative(),
    proRated: z.boolean(),
    isReasonRequired: z.boolean(),
  }),
  compassionateLeave: z.object({
    enabled: z.boolean(),
    maxDaysPerYear: z.number().nonnegative(),
    isReasonRequired: z.boolean(),
  }),
  bereavementLeave: z.object({
    enabled: z.boolean(),
    maxDaysPerOccurrence: z.number().nonnegative(),
    isReasonRequired: z.boolean(),
  }),
  juryDutyLeave: z.object({
    enabled: z.boolean(),
    isReasonRequired: z.boolean(),
  }),
  electionLeave: z.object({
    enabled: z.boolean(),
    maxDaysPerYear: z.number().nonnegative(),
    isReasonRequired: z.boolean(),
  }),
  bandBusinessLeave: z.object({
    enabled: z.boolean(),
    isReasonRequired: z.boolean(),
  }),
  governanceLeave: z.object({
    enabled: z.boolean(),
    maxDaysPerMonth: z.number().nonnegative(),
    isReasonRequired: z.boolean(),
  }),
  educationLeave: z.object({
    enabled: z.boolean(),
    maxDaysPerYear: z.number().nonnegative(),
    isReasonRequired: z.boolean(),
  }),
  professionalDevelopmentLeave: z.object({
    enabled: z.boolean(),
    maxDaysPerYear: z.number().nonnegative(),
    isReasonRequired: z.boolean(),
  }),
  culturalSpiritualLeave: z.object({
    enabled: z.boolean(),
    maxDaysPerYear: z.number().nonnegative(),
    isReasonRequired: z.boolean(),
  }),
  leaveWithoutPay: z.object({
    enabled: z.boolean(),
    requiresApproval: z.boolean(),
    isReasonRequired: z.boolean(),
  }),
  approvalWorkflow: z.object({
    requireSupervisorApproval: z.boolean(),
    flagToDirectorThreshold: z.number().int().nonnegative(),
    autoApproveUnderDays: z.number().nonnegative(),
  }),
  createdBy: z.string().optional(),
  updatedBy: z.string().optional(),
});

type LeaveConfigValues = z.infer<typeof leaveConfigSchema>;

type Props = {
  onClose?: () => void;
  orgId?: string;
  selectedLeaveKey?: string | null;
  configLevel?: string;
  entityId?: string;
  configYear?: number;
};

export default function UpdateLeaveConfigurationForm({
  onClose,
  orgId,
  selectedLeaveKey = null,
  configLevel = "organization",
  entityId,
  configYear,
}: Props) {
  const { user } = useUser();
  const resolvedOrgForHook = user?.org?._id ?? user?.org?._id ?? defaultOrg;
  const currentUserId = user?._id ?? defaultUserId;

  const {
    data,
    isError,
    isLoading: isFetching,
  } = useGetLeaveConfiguration(
    resolvedOrgForHook,
    configLevel as any,
    entityId,
    configYear
  );

  const { mutate: updateLeaveConfiguration, isLoading } =
    useUpdateLeaveConfiguration(resolvedOrgForHook);

  // Get roles for role-based leave allocation
  const { data: rolesData } = useGetRolePermissions();
  const rolesRaw = rolesData?.data ?? [];
  const roleOptions = rolesRaw?.map((r: any) => ({
    value: r.role as string,
    label: formatRoleName(r.role as string),
  })) ?? [];

  const form = useForm<LeaveConfigValues>({
    validate: zodResolver(leaveConfigSchema),
    initialValues: {
      org: resolvedOrgForHook,
      annualLeave: {
        enabled: true,
        baseAllocation: 15,
        proRateByStartDate: true,
        scaleByServiceYears: true,
        serviceYearScale: [
        ],
        // changed to numeric percentage (0-100)
        carryForward: 0,
        isReasonRequired: false,
      },
      sickLeave: {
        enabled: true,
        accrualRate: 1.5,
        maxAccrual: 20,
        gracePeriodDays: 3,
        requireCertification: true,
        certificationAfterDays: 3,
        isReasonRequired: false,
      },
      pressingNecessity: {
        enabled: true,
        daysPerYear: 3,
        proRated: true,
        isReasonRequired: false,
      },
      compassionateLeave: {
        enabled: true,
        maxDaysPerYear: 5,
        isReasonRequired: false,
      },
      bereavementLeave: {
        enabled: true,
        maxDaysPerOccurrence: 5,
        isReasonRequired: false,
      },
      juryDutyLeave: {
        enabled: true,
        isReasonRequired: false,
      },
      electionLeave: {
        enabled: true,
        maxDaysPerYear: 1,
        isReasonRequired: false,
      },
      bandBusinessLeave: {
        enabled: true,
        isReasonRequired: false,
      },
      governanceLeave: {
        enabled: true,
        maxDaysPerMonth: 2,
        isReasonRequired: false,
      },
      educationLeave: {
        enabled: true,
        maxDaysPerYear: 10,
        isReasonRequired: false,
      },
      professionalDevelopmentLeave: {
        enabled: true,
        maxDaysPerYear: 5,
        isReasonRequired: false,
      },
      culturalSpiritualLeave: {
        enabled: true,
        maxDaysPerYear: 5,
        isReasonRequired: false,
      },
      leaveWithoutPay: {
        enabled: true,
        requiresApproval: true,
        isReasonRequired: false,
      },
      approvalWorkflow: {
        requireSupervisorApproval: true,
        flagToDirectorThreshold: 90,
        autoApproveUnderDays: 0,
      },
      createdBy: currentUserId,
      updatedBy: currentUserId,
    },
  });

  const getNested = (path: string) =>
    path
      .split(".")
      .reduce((acc: any, key: string) => acc?.[key], form.values as any);

  const setNested = (path: string, value: any) =>
    form.setFieldValue(path as any, value);

  useEffect(() => {
    if (!data?.data) return;

    const cfg = data.data as any;

    const mappedServiceYearScale =
      cfg?.annualLeave?.serviceYearScale?.map((s: any) => ({
        role: String(s.role ?? ""),
        minYears: Number(s.minYears ?? 0),
        maxYears: Number(s.maxYears ?? 0),
        days: Number(s.days ?? 0),
      })) ?? form.values.annualLeave.serviceYearScale;

    // carryForward is now days-based. Map any legacy server values sensibly:
    //  - "all"  → 365 (a day cap that still carries the full unused balance)
    //  - "N%"   → 0 (a percentage has no faithful day equivalent; surface it as
    //             unset so an admin consciously re-enters a day count instead of
    //             silently persisting a wrong day number)
    //  - number → used directly as days
    const rawCarryForward = cfg.annualLeave?.carryForward;
    let mappedCarryForward = form.values.annualLeave.carryForward;
    if (rawCarryForward !== undefined) {
      if (typeof rawCarryForward === "string" && rawCarryForward === "all") {
        mappedCarryForward = 365;
      } else if (
        typeof rawCarryForward === "string" &&
        rawCarryForward.trim().endsWith("%")
      ) {
        mappedCarryForward = 0;
      } else {
        mappedCarryForward = Number(rawCarryForward ?? mappedCarryForward);
        if (Number.isNaN(mappedCarryForward))
          mappedCarryForward = form.values.annualLeave.carryForward;
      }
    }

    const mappedValues: Partial<LeaveConfigValues> = {
      org: cfg.org ?? resolvedOrgForHook,
      annualLeave: {
        enabled: Boolean(cfg.annualLeave?.enabled ?? true),
        baseAllocation: Number(cfg.annualLeave?.baseAllocation ?? 0),
        proRateByStartDate: Boolean(
          cfg.annualLeave?.proRateByStartDate ??
          form.values.annualLeave.proRateByStartDate,
        ),
        scaleByServiceYears: Boolean(
          cfg.annualLeave?.scaleByServiceYears ??
          form.values.annualLeave.scaleByServiceYears,
        ),
        serviceYearScale: mappedServiceYearScale,
        carryForward: mappedCarryForward,
        isReasonRequired: Boolean(cfg.annualLeave?.isReasonRequired ?? false),
      },
      sickLeave: {
        enabled: Boolean(cfg.sickLeave?.enabled ?? true),
        accrualRate: Number(cfg.sickLeave?.accrualRate ?? 0),
        maxAccrual: Number(cfg.sickLeave?.maxAccrual ?? 0),
        gracePeriodDays: Number(cfg.sickLeave?.gracePeriodDays ?? 0),
        requireCertification: Boolean(
          cfg.sickLeave?.requireCertification ?? false,
        ),
        certificationAfterDays: Number(
          cfg.sickLeave?.certificationAfterDays ?? 0,
        ),
        isReasonRequired: Boolean(cfg.sickLeave?.isReasonRequired ?? false),
      },
      pressingNecessity: {
        enabled: Boolean(cfg.pressingNecessity?.enabled ?? true),
        daysPerYear: Number(cfg.pressingNecessity?.daysPerYear ?? 0),
        proRated: Boolean(cfg.pressingNecessity?.proRated ?? false),
        isReasonRequired: Boolean(
          cfg.pressingNecessity?.isReasonRequired ?? false,
        ),
      },
      compassionateLeave: {
        enabled: Boolean(cfg.compassionateLeave?.enabled ?? true),
        maxDaysPerYear: Number(cfg.compassionateLeave?.maxDaysPerYear ?? 0),
        isReasonRequired: Boolean(
          cfg.compassionateLeave?.isReasonRequired ?? false,
        ),
      },
      bereavementLeave: {
        enabled: Boolean(cfg.bereavementLeave?.enabled ?? true),
        maxDaysPerOccurrence: Number(
          cfg.bereavementLeave?.maxDaysPerOccurrence ?? 0,
        ),
        isReasonRequired: Boolean(
          cfg.bereavementLeave?.isReasonRequired ?? false,
        ),
      },
      juryDutyLeave: {
        enabled: Boolean(cfg.juryDutyLeave?.enabled ?? true),
        isReasonRequired: Boolean(cfg.juryDutyLeave?.isReasonRequired ?? false),
      },
      electionLeave: {
        enabled: Boolean(cfg.electionLeave?.enabled ?? true),
        maxDaysPerYear: Number(cfg.electionLeave?.maxDaysPerYear ?? 0),
        isReasonRequired: Boolean(cfg.electionLeave?.isReasonRequired ?? false),
      },
      bandBusinessLeave: {
        enabled: Boolean(cfg.bandBusinessLeave?.enabled ?? true),
        isReasonRequired: Boolean(
          cfg.bandBusinessLeave?.isReasonRequired ?? false,
        ),
      },
      governanceLeave: {
        enabled: Boolean(cfg.governanceLeave?.enabled ?? true),
        maxDaysPerMonth: Number(cfg.governanceLeave?.maxDaysPerMonth ?? 0),
        isReasonRequired: Boolean(
          cfg.governanceLeave?.isReasonRequired ?? false,
        ),
      },
      educationLeave: {
        enabled: Boolean(cfg.educationLeave?.enabled ?? true),
        maxDaysPerYear: Number(cfg.educationLeave?.maxDaysPerYear ?? 0),
        isReasonRequired: Boolean(
          cfg.educationLeave?.isReasonRequired ?? false,
        ),
      },
      professionalDevelopmentLeave: {
        enabled: Boolean(cfg.professionalDevelopmentLeave?.enabled ?? true),
        maxDaysPerYear: Number(
          cfg.professionalDevelopmentLeave?.maxDaysPerYear ?? 0,
        ),
        isReasonRequired: Boolean(
          cfg.professionalDevelopmentLeave?.isReasonRequired ?? false,
        ),
      },
      culturalSpiritualLeave: {
        enabled: Boolean(cfg.culturalSpiritualLeave?.enabled ?? true),
        maxDaysPerYear: Number(cfg.culturalSpiritualLeave?.maxDaysPerYear ?? 0),
        isReasonRequired: Boolean(
          cfg.culturalSpiritualLeave?.isReasonRequired ?? false,
        ),
      },
      leaveWithoutPay: {
        enabled: Boolean(cfg.leaveWithoutPay?.enabled ?? true),
        requiresApproval: Boolean(
          cfg.leaveWithoutPay?.requiresApproval ?? true,
        ),
        isReasonRequired: Boolean(
          cfg.leaveWithoutPay?.isReasonRequired ?? false,
        ),
      },
      approvalWorkflow: {
        requireSupervisorApproval: Boolean(
          cfg.approvalWorkflow?.requireSupervisorApproval ?? true,
        ),
        flagToDirectorThreshold: Number(
          cfg.approvalWorkflow?.flagToDirectorThreshold ?? 0,
        ),
        autoApproveUnderDays: Number(
          cfg.approvalWorkflow?.autoApproveUnderDays ?? 0,
        ),
      },
      createdBy: cfg.createdBy ?? currentUserId,
      updatedBy: currentUserId,
    };

    form.setValues({
      ...form.values,
      ...(mappedValues as LeaveConfigValues),
    });
  }, [data]);

  const addServiceScale = () =>
    form.insertListItem("annualLeave.serviceYearScale", {
      role: "",
      minYears: 0,
      maxYears: 0,
      days: 0,
    });

  const removeServiceScale = (index: number) =>
    form.removeListItem("annualLeave.serviceYearScale", index);

  const buildPayloadForSubmit = (values: LeaveConfigValues) => {
    const sanitizeServiceYearScale = (arr: any[]) =>
      (arr || []).map((s: any) => ({
        role: String(s.role ?? ""),
        minYears: Number(s.minYears ?? 0),
        maxYears: Number(s.maxYears ?? 0),
        days: Number(s.days ?? 0),
      }));

    // Multi-level support: always include level, entityId and year
    const levelData: any = {};
    if (configLevel) levelData.level = configLevel;
    if (entityId) levelData.entityId = entityId;
    if (configYear != null) levelData.year = configYear;

    if (selectedLeaveKey && (values as any)[selectedLeaveKey] !== undefined) {
      const sectionValue = JSON.parse(
        JSON.stringify((values as any)[selectedLeaveKey]),
      );

      if (
        selectedLeaveKey === "annualLeave" &&
        Array.isArray(sectionValue?.serviceYearScale)
      ) {
        sectionValue.serviceYearScale = sanitizeServiceYearScale(
          sectionValue.serviceYearScale,
        );
      }

      return {
        ...levelData,
        [selectedLeaveKey]: sectionValue,
      } as any;
    }

    const {
      org: _org,
      createdBy: _createdBy,
      updatedBy: _updatedBy,
      ...rest
    } = values as any;

    const full = {
      ...levelData,
      ...rest,
      annualLeave: {
        ...rest.annualLeave,
        serviceYearScale: sanitizeServiceYearScale(
          rest.annualLeave?.serviceYearScale || [],
        ),
      },
    } as any;

    return full;
  };

  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [pendingValues, setPendingValues] = useState<LeaveConfigValues | null>(
    null,
  );

  const handleSubmit = (values: LeaveConfigValues) => {
    setPendingValues(values);
    setConfirmModalOpen(true);
  };

  const handleConfirmYes = () => {
    if (!pendingValues) return;
    const values = pendingValues;
    const resolvedOrg = resolvedOrgForHook;
    if (!resolvedOrg) {
      console.error("No org id available at submit time. Aborting.");
      return;
    }

    const payload = buildPayloadForSubmit(values);
    console.log(payload, "payload ready -> sending via mutate({id, data})");

    updateLeaveConfiguration(
      { id: resolvedOrg, data: payload },
      {
        onSuccess: (res) => {
          console.log("Leave config update success:", res);
          setConfirmModalOpen(false);
          setPendingValues(null);
          onClose?.();
        },
        onError: (err: any) => {
          console.error("Leave config update error:", err);
          setConfirmModalOpen(false);
          setPendingValues(null);
          onClose?.();
          if (err?.response) {
            console.error("server error body:", err.response.data);
          }
        },
      },
    );
  };

  const handleConfirmNo = () => {
    setPendingValues(null);
    setConfirmModalOpen(false);
  };

  const shouldShow = (key: string) => {
    return !selectedLeaveKey || selectedLeaveKey === key;
  };

  if (isFetching) return <div>Loading configuration...</div>;
  if (isError) return <div>Error loading configuration</div>;

  const CarryForwardControl = ({ path }: { path: string }) => {
    const val = getNested(path) as any;
    const dayValue = typeof val === "number" ? (val as number) : undefined;

    return (
      <Group spacing="sm" align="center">
        <NumberInput
          label="Carry Forward (days)"
          min={0}
          max={365}
          value={dayValue}
          onChange={(v) => {
            if (v == null) return;
            setNested(path, Math.round(v));
          }}
          style={{ width: 160 }}
        />
      </Group>
    );
  };

  return (
    <>
      <form onSubmit={form.onSubmit(handleSubmit)}>
        <Stack spacing="md">
          <Title order={4}>Leave Configuration</Title>

          <Grid>
            {shouldShow("annualLeave") && (
              <Grid.Col span={12}>
                <Divider my="sm" label="Annual Leave" labelPosition="left" />
                <Group align="center" spacing="md">
                  <Switch
                    label="Enabled"
                    {...form.getInputProps("annualLeave.enabled", {
                      type: "checkbox",
                    })}
                  />
                  <NumberInput
                    label="Base Allocation (days)"
                    min={0}
                    step={0.1}
                    precision={1}
                    {...form.getInputProps("annualLeave.baseAllocation")}
                  />
                  <Switch
                    label="Pro-rate by start date"
                    {...form.getInputProps("annualLeave.proRateByStartDate", {
                      type: "checkbox",
                    })}
                  />
                  <Switch
                    label="Scale by service years"
                    {...form.getInputProps("annualLeave.scaleByServiceYears", {
                      type: "checkbox",
                    })}
                  />
                  <Switch
                    label="Is Reason Required"
                    {...form.getInputProps("annualLeave.isReasonRequired", {
                      type: "checkbox",
                    })}
                  />
                </Group>

                <Box mt="sm">
                  <Title order={6}>Service Year Scale</Title>
                  {form.values.annualLeave.serviceYearScale.map((item, idx) => (
                    <Box key={idx} mt="xs" p="sm" style={{ border: '1px solid #e9ecef', borderRadius: '4px' }}>
                      <Group spacing="sm" align="end">
                        <Select
                          label="Role"
                          placeholder="Select a role"
                          searchable
                          data={roleOptions}
                          style={{ flex: 1 }}
                          {...form.getInputProps(
                            `annualLeave.serviceYearScale.${idx}.role`
                          )}
                        />
                        <NumberInput
                          label="Min Years"
                          min={0}
                          {...form.getInputProps(
                            `annualLeave.serviceYearScale.${idx}.minYears`
                          )}
                        />
                        <NumberInput
                          label="Max Years"
                          min={0}
                          {...form.getInputProps(
                            `annualLeave.serviceYearScale.${idx}.maxYears`
                          )}
                        />
                        <NumberInput
                          label="Days"
                          min={0}
                          step={0.1}
                          precision={1}
                          style={{ width: 150 }}
                          {...form.getInputProps(
                            `annualLeave.serviceYearScale.${idx}.days`
                          )}
                        />
                        <ActionIcon
                          color="red"
                          variant="light"
                          onClick={() => removeServiceScale(idx)}
                          aria-label="Remove scale row"
                        >
                          <IconTrash size={16} />
                        </ActionIcon>
                      </Group>
                    </Box>
                  ))}

                  <Button
                    variant="outline"
                    mt="sm"
                    leftIcon={<IconPlus size={16} />}
                    onClick={addServiceScale}
                    type="button"
                  >
                    Add Scale Row
                  </Button>
                </Box>

                <Box mt="sm">
                  <CarryForwardControl path="annualLeave.carryForward" />
                </Box>
              </Grid.Col>
            )}

            {shouldShow("sickLeave") && (
              <Grid.Col span={12}>
                <Divider my="sm" label="Sick Leave" labelPosition="left" />
                <Group spacing="md" align="center">
                  <Switch
                    label="Enabled"
                    {...form.getInputProps("sickLeave.enabled", {
                      type: "checkbox",
                    })}
                  />
                  <NumberInput
                    label="Accrual Rate"
                    step={0.1}
                    precision={1}
                    {...form.getInputProps("sickLeave.accrualRate")}
                  />
                  <NumberInput
                    label="Max Accrual (days)"
                    step={0.1}
                    precision={1}
                    {...form.getInputProps("sickLeave.maxAccrual")}
                  />
                  <NumberInput
                    label="Grace Period (days)"
                    step={0.1}
                    precision={1}
                    {...form.getInputProps("sickLeave.gracePeriodDays")}
                  />
                  <Switch
                    label="Require Certification"
                    {...form.getInputProps("sickLeave.requireCertification", {
                      type: "checkbox",
                    })}
                  />
                  <NumberInput
                    label="Certification After (days)"
                    step={0.1}
                    precision={1}
                    {...form.getInputProps("sickLeave.certificationAfterDays")}
                  />
                  <Switch
                    label="Is Reason Required"
                    {...form.getInputProps("sickLeave.isReasonRequired", {
                      type: "checkbox",
                    })}
                  />
                </Group>
              </Grid.Col>
            )}

            {shouldShow("pressingNecessity") && (
              <Grid.Col span={12}>
                <Divider
                  my="sm"
                  label="Pressing Necessity"
                  labelPosition="left"
                />
                <Group spacing="md" align="center">
                  <Switch
                    label="Enabled"
                    {...form.getInputProps("pressingNecessity.enabled", {
                      type: "checkbox",
                    })}
                  />
                  <NumberInput
                    label="Days Per Year"
                    step={0.1}
                    precision={1}
                    {...form.getInputProps("pressingNecessity.daysPerYear")}
                  />
                  <Switch
                    label="Pro-rated"
                    {...form.getInputProps("pressingNecessity.proRated", {
                      type: "checkbox",
                    })}
                  />
                  <Switch
                    label="Is Reason Required"
                    {...form.getInputProps(
                      "pressingNecessity.isReasonRequired",
                      {
                        type: "checkbox",
                      },
                    )}
                  />
                </Group>
              </Grid.Col>
            )}

            {shouldShow("compassionateLeave") && (
              <Grid.Col span={6}>
                <Divider
                  my="sm"
                  label="Compassionate Leave"
                  labelPosition="left"
                />
                <Group>
                  <Switch
                    label="Enabled"
                    {...form.getInputProps("compassionateLeave.enabled", {
                      type: "checkbox",
                    })}
                  />
                  <NumberInput
                    label="Max Days/Occurrence"
                    step={0.1}
                    precision={1}
                    {...form.getInputProps("compassionateLeave.maxDaysPerYear")}
                  />
                  <Switch
                    label="Is Reason Required"
                    {...form.getInputProps(
                      "compassionateLeave.isReasonRequired",
                      {
                        type: "checkbox",
                      },
                    )}
                  />
                </Group>
              </Grid.Col>
            )}

            {shouldShow("bereavementLeave") && (
              <Grid.Col span={6}>
                <Divider
                  my="sm"
                  label="Bereavement Leave"
                  labelPosition="left"
                />
                <Group>
                  <Switch
                    label="Enabled"
                    {...form.getInputProps("bereavementLeave.enabled", {
                      type: "checkbox",
                    })}
                  />
                  <NumberInput
                    label="Max Days/Occurrence"
                    step={0.1}
                    precision={1}
                    {...form.getInputProps(
                      "bereavementLeave.maxDaysPerOccurrence",
                    )}
                  />
                  <Switch
                    label="Is Reason Required"
                    {...form.getInputProps(
                      "bereavementLeave.isReasonRequired",
                      {
                        type: "checkbox",
                      },
                    )}
                  />
                </Group>
              </Grid.Col>
            )}

            {shouldShow("juryDutyLeave") && (
              <Grid.Col span={6}>
                {/* <Title order={6} mb="xs">
                  Jury Duty Leave
                </Title> */}

                <Box style={{ marginBottom: "3px" }}>
                  <Switch
                    label="Jury Duty Leave"
                    {...form.getInputProps("juryDutyLeave.enabled", {
                      type: "checkbox",
                    })}
                  />
                </Box>

                <Switch
                  label="Is Reason Required"
                  {...form.getInputProps("juryDutyLeave.isReasonRequired", {
                    type: "checkbox",
                  })}
                />
              </Grid.Col>
            )}

            {shouldShow("electionLeave") && (
              <Grid.Col span={6}>
                <Group>
                  <Switch
                    label="Election Leave"
                    {...form.getInputProps("electionLeave.enabled", {
                      type: "checkbox",
                    })}
                  />
                  {/* <NumberInput
                    label="Max Days/Year"
                    step={0.1}
                    precision={1}
                    {...form.getInputProps("electionLeave.maxDaysPerYear")}
                  /> */}
                  <Switch
                    label="Is Reason Required"
                    {...form.getInputProps("electionLeave.isReasonRequired", {
                      type: "checkbox",
                    })}
                  />
                </Group>
              </Grid.Col>
            )}

            {shouldShow("bandBusinessLeave") && (
              <Grid.Col span={6}>
                <Switch
                  label="Band Business Leave"
                  {...form.getInputProps("bandBusinessLeave.enabled", {
                    type: "checkbox",
                  })}
                />

                <Switch
                  label="Is Reason Required"
                  {...form.getInputProps("bandBusinessLeave.isReasonRequired", {
                    type: "checkbox",
                  })}
                />
              </Grid.Col>
            )}

            {shouldShow("governanceLeave") && (
              <Grid.Col span={6}>
                <Switch
                  label="Governance Leave"
                  {...form.getInputProps("governanceLeave.enabled", {
                    type: "checkbox",
                  })}
                />
                <NumberInput
                  label="max Days per month"
                  step={0.1}
                  precision={1}
                  {...form.getInputProps("governanceLeave.maxDaysPerMonth")}
                />
                <Switch
                  label="Is Reason Required"
                  {...form.getInputProps("governanceLeave.isReasonRequired", {
                    type: "checkbox",
                  })}
                />
              </Grid.Col>
            )}

            {shouldShow("educationLeave") && (
              <Grid.Col span={6}>
                <Group>
                  <Switch
                    label="Education Leave"
                    {...form.getInputProps("educationLeave.enabled", {
                      type: "checkbox",
                    })}
                  />
                  {/* <NumberInput
                    label="Max Days/Year"
                    step={0.1}
                    precision={1}
                    {...form.getInputProps("educationLeave.maxDaysPerYear")}
                  /> */}
                  <Switch
                    label="Is Reason Required"
                    {...form.getInputProps("educationLeave.isReasonRequired", {
                      type: "checkbox",
                    })}
                  />
                </Group>
              </Grid.Col>
            )}

            {shouldShow("professionalDevelopmentLeave") && (
              <Grid.Col span={8}>
                <Group>
                  <Switch
                    label="Professional Development"
                    {...form.getInputProps(
                      "professionalDevelopmentLeave.enabled",
                      {
                        type: "checkbox",
                      },
                    )}
                  />
                  {/* <NumberInput
                    label="Max Days/Year"
                    step={0.1}
                    precision={1}
                    {...form.getInputProps(
                      "professionalDevelopmentLeave.maxDaysPerYear"
                    )}
                  /> */}
                  <Switch
                    label="Is Reason Required"
                    {...form.getInputProps(
                      "professionalDevelopmentLeave.isReasonRequired",
                      {
                        type: "checkbox",
                      },
                    )}
                  />
                </Group>
              </Grid.Col>
            )}

            {shouldShow("culturalSpiritualLeave") && (
              <Grid.Col span={6}>
                <Group>
                  <Switch
                    label="Cultural / Spiritual"
                    {...form.getInputProps("culturalSpiritualLeave.enabled", {
                      type: "checkbox",
                    })}
                  />
                  <NumberInput
                    label="Max Days/Year"
                    step={0.1}
                    precision={1}
                    {...form.getInputProps(
                      "culturalSpiritualLeave.maxDaysPerYear",
                    )}
                  />
                  <Switch
                    label="Is Reason Required"
                    {...form.getInputProps(
                      "culturalSpiritualLeave.isReasonRequired",
                      {
                        type: "checkbox",
                      },
                    )}
                  />
                </Group>
              </Grid.Col>
            )}

            {shouldShow("leaveWithoutPay") && (
              <Grid.Col span={12}>
                <Divider
                  my="sm"
                  label="Leave Without Pay"
                  labelPosition="left"
                />
                <Group>
                  <Switch
                    label="Enabled"
                    {...form.getInputProps("leaveWithoutPay.enabled", {
                      type: "checkbox",
                    })}
                  />
                  <Switch
                    label="Requires Approval"
                    {...form.getInputProps("leaveWithoutPay.requiresApproval", {
                      type: "checkbox",
                    })}
                  />
                  <Switch
                    label="Is Reason Required"
                    {...form.getInputProps("leaveWithoutPay.isReasonRequired", {
                      type: "checkbox",
                    })}
                  />
                </Group>
              </Grid.Col>
            )}

            {shouldShow("approvalWorkflow") && (
              <Grid.Col span={12}>
                <Divider
                  my="sm"
                  label="Approval Workflow"
                  labelPosition="left"
                />
                <Group>
                  <Switch
                    label="Require Supervisor Approval"
                    {...form.getInputProps(
                      "approvalWorkflow.requireSupervisorApproval",
                      {
                        type: "checkbox",
                      },
                    )}
                  />
                  <NumberInput
                    label="Flag to Director Threshold (%)"
                    min={0}
                    max={100}
                    {...form.getInputProps(
                      "approvalWorkflow.flagToDirectorThreshold",
                    )}
                  />
                  <NumberInput
                    label="Auto-approve under days"
                    min={0}
                    step={0.1}
                    precision={1}
                    {...form.getInputProps(
                      "approvalWorkflow.autoApproveUnderDays",
                    )}
                  />
                </Group>
              </Grid.Col>
            )}
          </Grid>

          <Group position="right" spacing="sm" mt="md">
            <Button color="cancel" onClick={() => onClose?.()} type="button">
              Cancel
            </Button>
            <Button type="submit" loading={isLoading}>
              Update Configuration
            </Button>
          </Group>
        </Stack>
      </form>

      <Modal
        opened={confirmModalOpen}
        onClose={handleConfirmNo}
        centered
        withCloseButton={false}
        overlayOpacity={0.55}
        overlayBlur={3}
      >
        <Stack spacing="md" align="center">
          <Text size="md" align="center">
            User balance will be updated as per new applied configurations.
            <br />
            Are you sure you want to update this?
          </Text>
          <Group position="center" mt="md">
            <Button color="cancel" onClick={handleConfirmNo}>
              No
            </Button>
            <Button
              color="green"
              onClick={handleConfirmYes}
              loading={isLoading}
            >
              Yes
            </Button>
          </Group>
        </Stack>
      </Modal>
    </>
  );
}